import { isAccommodationShoppingCartItem, } from '@data/models/ShoppingCartItem';
import Store from '@data/stores/Store';
import { isVariableChargeType } from '@data/models/ChargeType';
import { persistentPropertyStorage } from '@data/stores/PersistentPropertyStorage';
import { isInIframe } from './window';
const createExternalOffers = (travelInsurance) => travelInsurance && travelInsurance.offerId !== 'none'
    ? [
        {
            offerId: travelInsurance.offerId,
            provider: travelInsurance.provider,
        },
    ]
    : [];
// The default value 'confirmed' is the same used on the backend in a similar function
export function isPendingReservationStatus(status = 'confirmed') {
    const pendingStatuses = [
        'call2confirm',
        'not_confirmed',
        'paypal_init',
        'pending_payment',
    ];
    return pendingStatuses.includes(status);
}
const groupAddons = (addons) => addons.reduce((acc, addon) => {
    if (isVariableChargeType(addon.item)) {
        acc.addonsForEachRoom.push({
            addon_id: addon.item.id,
            quantity: 1,
        });
    }
    else {
        acc.addonsOnlyForFirstRoom.push({
            addon_id: addon.item.id,
            quantity: addon.amount,
        });
    }
    return acc;
}, {
    addonsForEachRoom: [],
    addonsOnlyForFirstRoom: [],
});
const groupBookedAccommodations = (items) => items.reduce((acc, { id, model }) => {
    if (isAccommodationShoppingCartItem(model)) {
        if (model.item.sourceSettings.canSelectSpecificRoom) {
            acc.individuallyBookedAccommodations.push({ id, model });
        }
        else {
            acc.bookedAccommodations.push({ id, model });
        }
    }
    return acc;
}, {
    bookedAccommodations: [],
    individuallyBookedAccommodations: [],
});
const isRoomList = (rooms) => Array.isArray(rooms);
function buildRooms(items) {
    const rooms = {};
    const { bookedAccommodations, individuallyBookedAccommodations } = groupBookedAccommodations(items);
    const addonsForEachAccommodation = Store.property.cart.getAddonsForEachAccommodation();
    for (const item of bookedAccommodations) {
        const { id, model } = item;
        const rateId = model.item.rateId.toString();
        const addonsForAccommodation = addonsForEachAccommodation[id];
        const { addonsForEachRoom, addonsOnlyForFirstRoom } = groupAddons(addonsForAccommodation);
        const rateRooms = rooms[rateId];
        const roomList = isRoomList(rateRooms) ? rateRooms : [];
        rooms[rateId] = roomList;
        for (let i = 0; i < model.amount; i++) {
            roomList.push({
                addons: i === 0
                    ? [...addonsOnlyForFirstRoom, ...addonsForEachRoom]
                    : addonsForEachRoom,
                adults: model.adults.toString(),
                kids: model.kids.toString(),
            });
        }
    }
    for (const item of individuallyBookedAccommodations) {
        const { id, model } = item;
        const rateId = model.item.rateId.toString();
        const rateRooms = rooms[rateId];
        const roomsByUnit = rateRooms && !isRoomList(rateRooms) ? rateRooms : {};
        rooms[rateId] = roomsByUnit;
        const key = model.unit;
        const addons = Store.property.cart
            .findAddonsByAccommodationId(id)
            .map((x) => ({
            addon_id: x.model.item.id,
            quantity: x.model.amount,
        }));
        roomsByUnit[key] = {
            addons: addons.length > 0 ? addons : undefined,
            adults: model.adults.toString(),
            kids: model.kids.toString(),
        };
    }
    return rooms;
}
export function createReservationRequest({ aiSource, googleHotelAds, origin, payment, referralId, termsAccepted, widgetSource, }) {
    var _a, _b, _c;
    const property = Store.property.model;
    const items = Store.property.cart.items;
    const guests = [
        Store.property.guests.main,
        ...Store.property.guests.additional,
    ];
    const checkin = Store.property.cart.checkIn;
    const checkout = Store.property.cart.checkOut;
    // Validate required fields
    if (!property)
        throw new Error('Property is required');
    if (items.length < 1)
        throw new Error('Cart items are required');
    if (guests.length < 1)
        throw new Error('Guests are required');
    if (!checkin || !checkout)
        throw new Error('Check-in and Check-out dates are required');
    const language = Store.environment.language;
    const currency = Store.environment.currency;
    const code = (_b = (_a = Store.components.searchPanel) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.code;
    const propertyId = property.id;
    const cartToken = Store.session.cartToken || '';
    const rooms = buildRooms(items);
    // BENG-4165: never attach an external offer (CFAR) to the reservation when the
    // property has external offers disabled, even if a stale selection lingers in
    // the cart. `externalOffersEnabled` is the single authoritative CFAR signal.
    const externalOffersEnabled = !!property.externalOffersEnabled;
    const externalOffers = externalOffersEnabled
        ? createExternalOffers(Store.property.cart.travelInsurance)
        : [];
    const smartPolicyRootId = Store.property.cart.accommodations
        .map((cartItem) => cartItem.item.smartPolicyRootId)
        .filter(Boolean)[0];
    // Get Policy by smartPolicyRootId
    const smartPolicyId = (_c = Store.smartPolicies.policies[smartPolicyRootId]) === null || _c === void 0 ? void 0 : _c.id;
    const sessionId = persistentPropertyStorage.sessionId;
    const externalOffersShown = externalOffersEnabled &&
        !!Store.property.travelInsurance.offers.length &&
        !!Store.property.cart.travelInsurance;
    const bookingEngineSource = Store.environment.bookingEngineSource;
    return {
        aiSource,
        bookingEngineSource,
        cartToken,
        checkin,
        checkout,
        code,
        currency,
        externalOffers,
        externalOffersShown,
        googleHotelAds,
        guests,
        iframe: isInIframe(),
        language,
        origin,
        payment,
        propertyId,
        referralId,
        rooms,
        sessionId,
        smartPolicyId,
        smartPolicyRootId,
        termsAccepted,
        widgetSource,
    };
}
